import React, { useId, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DeviceSectionNav from '../devices/components/DeviceSectionNav';
import {
  findArticleByPath,
  parseHelpHeadings,
  replaceDocxReferencesForInApp,
  resolveHelpLinkTarget,
} from './helpPlaybook';
import { resolveHelpImageSrc } from './helpApi';
import { scrollToHelpHeading } from './helpScroll';
import './HelpMarkdown.css';

const SCREENSHOT_RE = /^\[Screenshot:\s*.+\]$/i;
const MIN_JUMP_SECTIONS = 3;

/**
 * Light preprocessing for in-app reading.
 * @param {string} markdown
 */
export function preprocessHelpMarkdown(markdown) {
  if (!markdown) return '';
  return replaceDocxReferencesForInApp(
    markdown
      .split('\n')
      .filter((line) => !/^\s*---\s*$/.test(line))
      .map((line) => {
        const trimmed = line.trim();
        if (SCREENSHOT_RE.test(trimmed)) {
          return `> ${trimmed}`;
        }
        return line;
      })
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/\s*·\s*\[[^\]]+\]\([^)]*artifact[^)]*\)/gi, '')
      .replace(/\[[^\]]+\]\([^)]*\.docx[^)]*\)/gi, '')
  );
}

function linkLabel(children, manifest, articlePath) {
  if (!articlePath || !manifest) return children;
  const childText = React.Children.toArray(children).join('');
  if (!/\.md$/i.test(childText.trim())) return children;
  const article = findArticleByPath(manifest, articlePath);
  if (article?.title) return article.title;
  return children;
}

function HelpMarkdownLink({
  href,
  children,
  manifest,
  currentArticlePath,
  onNavigateToArticle,
  onNavigateToHelpSection,
  onNavigateToHelpHome,
  ...props
}) {
  const target = useMemo(() => {
    if (!manifest || !currentArticlePath) return { type: 'unavailable' };
    return resolveHelpLinkTarget(manifest, href, currentArticlePath);
  }, [manifest, currentArticlePath, href]);

  if (href?.startsWith('#')) {
    const sectionId = href.slice(1);
    return (
      <button
        type="button"
        className="help-markdown-link"
        onClick={() => scrollToHelpHeading(sectionId)}
      >
        {children}
      </button>
    );
  }

  if (target.type === 'article' && onNavigateToArticle) {
    const label = linkLabel(children, manifest, target.article.path);
    return (
      <button
        type="button"
        className="help-markdown-link"
        onClick={() => onNavigateToArticle(target.article.id, target.hash)}
      >
        {label}
      </button>
    );
  }

  if (target.type === 'help-section' && onNavigateToHelpSection) {
    return (
      <button
        type="button"
        className="help-markdown-link"
        onClick={() => onNavigateToHelpSection(target.section)}
      >
        {children}
      </button>
    );
  }

  if (target.type === 'help-home' && onNavigateToHelpHome) {
    return (
      <button
        type="button"
        className="help-markdown-link"
        onClick={onNavigateToHelpHome}
      >
        {children}
      </button>
    );
  }

  if (target.type === 'external') {
    return (
      <a href={target.href} target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    );
  }

  if (href?.toLowerCase().includes('.md') || href?.includes('artifact/')) {
    return null;
  }

  return null;
}

function HelpMarkdownImage({ src, alt, title, ...props }) {
  const captionId = useId();
  const resolvedSrc = resolveHelpImageSrc(src);
  const caption = (alt || title || '').trim();
  if (!resolvedSrc) return null;

  if (!caption) {
    return <img src={resolvedSrc} alt="" {...props} />;
  }

  return (
    <figure className="help-markdown-figure" aria-labelledby={captionId}>
      <img src={resolvedSrc} alt="" {...props} />
      <figcaption id={captionId} className="help-markdown-figcaption">
        {caption}
      </figcaption>
    </figure>
  );
}

function createHeadingComponent(level, headingIndexRef, headingIds) {
  const Tag = `h${level}`;

  return function HelpMarkdownHeading({ children, ...props }) {
    let id;
    if (level >= 2 && level <= 3) {
      id = headingIds[headingIndexRef.current];
      headingIndexRef.current += 1;
    }

    return (
      <Tag id={id} {...props}>
        {children}
      </Tag>
    );
  };
}

function MarkdownHelpViewer({
  markdown,
  className = '',
  manifest = null,
  currentArticlePath = null,
  onNavigateToArticle = null,
  onNavigateToHelpSection = null,
  onNavigateToHelpHome = null,
}) {
  const processed = useMemo(() => preprocessHelpMarkdown(markdown), [markdown]);
  const headings = useMemo(() => parseHelpHeadings(processed), [processed]);
  const headingIds = useMemo(() => headings.map((heading) => heading.id), [headings]);
  const headingIndexRef = useRef(0);
  headingIndexRef.current = 0;

  const jumpLinks = useMemo(
    () =>
      headings
        .filter((heading) => heading.level === 2)
        .map((heading) => ({ id: heading.id, label: heading.title })),
    [headings]
  );

  const markdownComponents = useMemo(
    () => ({
      h2: createHeadingComponent(2, headingIndexRef, headingIds),
      h3: createHeadingComponent(3, headingIndexRef, headingIds),
      img: (imgProps) => <HelpMarkdownImage {...imgProps} />,
      a: (linkProps) => (
        <HelpMarkdownLink
          {...linkProps}
          manifest={manifest}
          currentArticlePath={currentArticlePath}
          onNavigateToArticle={onNavigateToArticle}
          onNavigateToHelpSection={onNavigateToHelpSection}
          onNavigateToHelpHome={onNavigateToHelpHome}
        />
      ),
    }),
    [
      headingIds,
      manifest,
      currentArticlePath,
      onNavigateToArticle,
      onNavigateToHelpSection,
      onNavigateToHelpHome,
    ]
  );

  return (
    <div className={`help-markdown ${className}`.trim()}>
      {jumpLinks.length >= MIN_JUMP_SECTIONS && (
        <DeviceSectionNav links={jumpLinks} onNavigate={scrollToHelpHeading} />
      )}
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {processed}
      </ReactMarkdown>
    </div>
  );
}

export default MarkdownHelpViewer;
